import { useOrgStore } from '@/stores/org-store';
import { apiRequest } from '../../utils/api-request';
import {
  Collection,
  CollectionSource,
  CollectionSourceConfig,
  ExternalChatFilterDef,
} from '@/types/source-types';

export interface UpdateCollectionResponse {
  data?: Collection;
  detail?: string;
}

interface UpdateCollectionProps {
  id: string;
  name?: string;
  description?: string;
  source?: CollectionSource;
  sourceConfig?: CollectionSourceConfig | null;
  config?: Record<string, unknown>;
  exposedUploadAttributes?: string[] | null;
  exposedUploadAttributeCardinality?: Record<string, 'single' | 'multi'> | null;
  externalChatFilters?: ExternalChatFilterDef[] | null;
}

export const updateCollection = async ({
  id = '',
  name,
  description,
  source,
  sourceConfig,
  config,
  exposedUploadAttributes,
  exposedUploadAttributeCardinality,
  externalChatFilters,
}: UpdateCollectionProps): Promise<UpdateCollectionResponse> => {
  const orgId = useOrgStore.getState().currentOrg?.orgid;

  if (!orgId) {
    throw new Error('Organization ID is required');
  }

  const payload: Record<string, unknown> = {};

  if (typeof name === 'string') {
    payload.name = name;
  }

  if (typeof description === 'string') {
    payload.description = description;
  }

  if (source) {
    payload.source = source;
  }

  if (sourceConfig !== undefined) {
    if (sourceConfig === null) {
      payload.source_config = null;
    } else {
      const cleanedConfig = JSON.parse(JSON.stringify(sourceConfig));
      if (Object.keys(cleanedConfig).length) {
        payload.source_config = cleanedConfig;
      }
    }
  }

  if (config !== undefined) {
    payload.config = config;
  }

  if (exposedUploadAttributes !== undefined) {
    payload.exposed_upload_attributes = exposedUploadAttributes;
  }

  if (exposedUploadAttributeCardinality !== undefined) {
    payload.exposed_upload_attribute_cardinality =
      exposedUploadAttributeCardinality;
  }

  if (externalChatFilters !== undefined) {
    payload.external_chat_filters = externalChatFilters;
  }

  const response = await apiRequest<UpdateCollectionResponse>({
    url: `/collections/${orgId}/${id}`,
    method: 'PATCH',
    body: payload,
    target: 'platform',
  });

  if (response?.detail) {
    return {
      detail: response.detail || 'Failed to update collection',
    };
  }

  return response as UpdateCollectionResponse;
};
